import { usePersistedState } from "@/lib/persistedFilters";
// Analytics panel — funnel, sentiment donut, and per-campaign breakdown for
// the AI Outbound module. Supports date + campaign filtering plus a PDF export.
//
// Extracted from AIOutbound.jsx during the P1 refactor.

import { useEffect, useMemo, useState } from "react";
import {
  PhoneOutgoing, PhoneCall, CalendarCheck, Timer, Loader2, Download, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { MetricCard, StatusBadge } from "./shared";

export function AnalyticsPanel({ campaigns }) {
  const [data, setData] = useState(null);
  const [campaignFilter, setCampaignFilter] = usePersistedState("ai-outbound-analytics.campaign", "all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const params = {};
    if (campaignFilter !== "all") params.campaign_id = campaignFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.get("/outbound/analytics", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [campaignFilter, startDate, endDate]);

  const clearFilters = () => { setCampaignFilter("all"); setStartDate(""); setEndDate(""); };
  const hasFilters = campaignFilter !== "all" || startDate || endDate;

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (campaignFilter !== "all") params.campaign_id = campaignFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/outbound/analytics/export.pdf", { params });
      if (!data?.token) throw new Error("no token");
      await downloadFromUrl(`/files/${data.token}`);
      toast.success("Analytics PDF exported");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  const totals = data?.totals || {};
  const funnelSteps = [
    { key: "recipients_called", label: "Attempted", color: "bg-slate-400" },
    { key: "connected",         label: "Connected", color: "bg-sky-500" },
    { key: "booked",            label: "Booked",    color: "bg-emerald-500" },
  ];
  const maxVal = Math.max(1, ...funnelSteps.map((s) => totals[s.key] || 0));
  const sentiment = data?.sentiment || { positive: 0, neutral: 0, negative: 0 };
  const sTotal = Math.max(1, sentiment.positive + sentiment.neutral + sentiment.negative);

  const perCampaign = useMemo(() => {
    const map = new Map((data?.campaigns || []).map((c) => [c.campaign_id, c]));
    // When a single campaign is filtered, show only that one row
    const base = campaignFilter === "all" ? (campaigns || []) : (campaigns || []).filter((c) => c.campaign_id === campaignFilter);
    return base.map((c) => ({ ...c, status: map.get(c.campaign_id)?.status || c.status }));
  }, [data, campaigns, campaignFilter]);

  return (
    <div className="space-y-4">
      <Card className="border border-border p-3" data-testid="analytics-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <Label className="text-xs font-medium text-muted-foreground">Campaign</Label>
            <Select value={campaignFilter} onValueChange={setCampaignFilter}>
              <SelectTrigger data-testid="analytics-filter-campaign" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {(campaigns || []).map((c) => (
                  <SelectItem key={c.campaign_id} value={c.campaign_id} data-testid={`analytics-filter-campaign-${c.campaign_id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Start date</Label>
            <Input type="date" data-testid="analytics-filter-start" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} className="h-9 w-40" />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">End date</Label>
            <Input type="date" data-testid="analytics-filter-end" value={endDate}
              onChange={(e) => setEndDate(e.target.value)} className="h-9 w-40" />
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="analytics-filter-clear" className="h-9">
              <X className="h-3.5 w-3.5" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting}
            data-testid="analytics-export-pdf-btn" className="h-9 ml-auto">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export PDF
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard icon={PhoneOutgoing} label="Campaigns" value={totals.campaigns ?? 0} />
        <MetricCard icon={PhoneCall} label="Attempts" value={totals.recipients_called ?? 0} />
        <MetricCard icon={CalendarCheck} label="Booked" value={totals.booked ?? 0} tone="emerald" />
        <MetricCard icon={Timer} label="Minutes" value={`${Number(totals.total_minutes || 0).toFixed(1)}`} tone="amber" />
      </div>

      {/* KPI rates — computed from the totals above, no extra fetch. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="analytics-kpi-rates">
        <MetricCard icon={PhoneCall} label="Connect rate"
          value={totals.recipients_called ? `${Math.round((totals.connected / totals.recipients_called) * 100)}%` : "—"}
          tone="sky"
          data-testid="analytics-kpi-connect-rate" />
        <MetricCard icon={CalendarCheck} label="Book rate"
          value={totals.connected ? `${Math.round((totals.booked / totals.connected) * 100)}%` : "—"}
          tone="emerald"
          data-testid="analytics-kpi-book-rate" />
        <MetricCard icon={Timer} label="Minutes / booked call"
          value={totals.booked ? `${(Number(totals.total_minutes || 0) / totals.booked).toFixed(1)}` : "—"}
          tone="amber"
          data-testid="analytics-kpi-min-per-booked" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border border-slate-200 p-4" data-testid="ai-outbound-funnel">
          <div className="mb-3 font-heading text-sm font-semibold text-slate-800">Funnel</div>
          <div className="space-y-2">
            {funnelSteps.map((s) => {
              const v = totals[s.key] || 0;
              const pct = Math.round((v / maxVal) * 100);
              return (
                <div key={s.key} data-testid={`ai-outbound-funnel-${s.key}`}>
                  <div className="mb-0.5 flex items-center justify-between text-xs text-slate-500">
                    <span>{s.label}</span>
                    <span className="font-semibold text-slate-700">{v}</span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-slate-100">
                    <div className={`h-3 rounded-full ${s.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="border border-slate-200 p-4" data-testid="ai-outbound-sentiment">
          <div className="mb-3 font-heading text-sm font-semibold text-slate-800">Sentiment</div>
          <div className="flex items-center gap-6">
            <SentimentDonut positive={sentiment.positive} neutral={sentiment.neutral} negative={sentiment.negative} />
            <dl className="space-y-1 text-xs">
              <LegendRow color="#10b981" label="Positive" value={sentiment.positive} total={sTotal} />
              <LegendRow color="#94a3b8" label="Neutral"  value={sentiment.neutral}  total={sTotal} />
              <LegendRow color="#ef4444" label="Negative" value={sentiment.negative} total={sTotal} />
            </dl>
          </div>
        </Card>
      </div>

      <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-per-campaign">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800">Per-campaign breakdown</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Campaign</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Attempts</th>
              <th className="px-3 py-2 text-right">Booked</th>
              <th className="px-3 py-2 text-right">Book rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {perCampaign.map((c) => {
              const rate = c.attempt_count ? Math.round((c.booked_count / c.attempt_count) * 1000) / 10 : 0;
              return (
                <tr key={c.campaign_id} data-testid={`ai-outbound-per-camp-${c.campaign_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                  <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-2 text-right text-slate-700">{c.attempt_count || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{c.booked_count || 0}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{rate}%</td>
                </tr>
              );
            })}
            {perCampaign.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-slate-400">No campaigns yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SentimentDonut({ positive, neutral, negative }) {
  const total = Math.max(1, positive + neutral + negative);
  const segs = [
    { color: "#10b981", val: positive },
    { color: "#94a3b8", val: neutral },
    { color: "#ef4444", val: negative },
  ];
  const r = 40, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110" data-testid="ai-outbound-sentiment-donut">
      <g transform="translate(55,55) rotate(-90)">
        <circle cx="0" cy="0" r={r} fill="none" stroke="#f1f5f9" strokeWidth="14" />
        {segs.map((s, i) => {
          const len = (s.val / total) * c;
          const el = <circle key={i} cx="0" cy="0" r={r} fill="none" stroke={s.color} strokeWidth="14"
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} />;
          offset += len;
          return el;
        })}
      </g>
      <text x="55" y="60" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">{positive + neutral + negative}</text>
    </svg>
  );
}

function LegendRow({ color, label, value, total }) {
  const pct = Math.round((value / total) * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      <span className="w-16 text-slate-500">{label}</span>
      <span className="font-semibold text-slate-700">{value}</span>
      <span className="text-slate-400">({pct}%)</span>
    </div>
  );
}
